import {replayIntegration, browserTracingIntegration, init as SentryInit} from "@sentry/remix";
import React, { useState, StrictMode, useEffect } from 'react';
import { hydrateRoot } from 'react-dom/client'
import { CacheProvider } from '@emotion/react'
import { RemixBrowser, useLocation, useMatches } from '@remix-run/react';
import { ClientStyleContext } from './context'
import createEmotionCache, { defaultCache } from './createEmotionCache'

SentryInit({
    // @ts-expect-error: need to define global window type
    dsn: window.ENV?.SENTRY_DSN || '',
    tracesSampleRate: 1,
    integrations: [browserTracingIntegration({
      useEffect,
      useLocation,
      useMatches
    }), 
    replayIntegration({ 
      maskAllText: true,
      blockAllMedia: true
    })],

    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 0.1
})

interface ClientCacheProviderProps {
  children: React.ReactNode;
}

function ClientCacheProvider({ children }: ClientCacheProviderProps) {
  const [cache, setCache] = useState(defaultCache)

  function reset() {
    setCache(createEmotionCache())
  }

  return (
    <ClientStyleContext.Provider value={{ reset }}>
      <CacheProvider value={cache}>{children}</CacheProvider>
    </ClientStyleContext.Provider>
  )
}

const hydrate = () => {
  React.startTransition(() => {
    hydrateRoot(
      document,
      <StrictMode>
        <ClientCacheProvider>
          <RemixBrowser />
        </ClientCacheProvider>
      </StrictMode>
    )
  })
}

if (window.requestIdleCallback) {
  window.requestIdleCallback(hydrate)
} else {
  // Safari doesn't support requestIdleCallback
  // https://caniuse.com/requestidlecallback
  setTimeout(hydrate, 1)
}